/**
 * Connection Service
 * Handles QR-based user connections via Octopus API
 */

/**
 * Check if Gig Genie app is installed on the device
 * Uses custom URL scheme or user agent detection
 */
export function isAppInstalled(): boolean {
  // Method 1: Check for custom URL scheme support
  if (typeof window !== 'undefined') {
    // Try to detect app via user agent or localStorage flag
    const appInstalled = localStorage.getItem('gigGenieAppInstalled') === 'true';
    
    // Method 2: Check for deep link capability
    const hasDeepLink = window.navigator.userAgent.includes('GigGenie') || 
                       document.referrer.includes('giggenie://');
    
    return appInstalled || hasDeepLink;
  }
  return false;
}

/**
 * Trigger app deep link to handle connection
 */
export function openAppForConnection(userID: string): void {
  const deepLink = `giggenie://connect?userID=${userID}`;
  
  // Try to open app
  window.location.href = deepLink;
  
  // Fallback: If app doesn't open, redirect to app store
  setTimeout(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      window.location.href = 'https://apps.apple.com/app/gig-genie'; // Update with actual App Store URL
    } else if (userAgent.includes('android')) {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.giggenie'; // Update with actual Play Store URL
    }
  }, 2500);
}

/**
 * Create connection via Octopus API
 */
export async function createConnection(
  scannerUserID: string,
  scannedUserID: string
): Promise<void> {
  try {
    const octopusApiUrl = (import.meta as any).env?.VITE_OCTOPUS_API_URL || 'https://api.octopus.example.com';
    const apiKey = (import.meta as any).env?.VITE_OCTOPUS_API_KEY || '';
    
    const response = await fetch(`${octopusApiUrl}/gig-connect/connections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        scannerUserID,
        scannedUserID,
        source: 'qr_scan',
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`Connection failed: ${response.statusText}`);
    }

    console.log('Connection created successfully');
  } catch (error) {
    console.error('Error creating connection:', error);
    throw error;
  }
}

/**
 * Save connection to Firestore
 */
export async function saveConnectionToFirestore(
  scannerUserID: string,
  scannedUserID: string
): Promise<void> {
  try {
    const { collection, addDoc, serverTimestamp, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../config/firebaseConfig');

    // Check for duplicate
    const connectionQuery = query(
      collection(db, 'gigConnectConnections'),
      where('scannerUserID', '==', scannerUserID),
      where('scannedUserID', '==', scannedUserID)
    );
    const existingDocs = await getDocs(connectionQuery);

    if (!existingDocs.empty) {
      console.log('Connection already exists');
      return;
    }

    await addDoc(collection(db, 'gigConnectConnections'), {
      scannerUserID,
      scannedUserID,
      createdAt: serverTimestamp(),
      source: 'qr_scan',
      status: 'connected'
    });

    console.log('Connection saved to Firestore');
  } catch (error) {
    console.error('Error saving connection:', error);
    throw error;
  }
}

